import { useState } from 'react'
import { supabase } from '../supabase'

export default function Onboarding({ session, onComplete }) {
  const [step, setStep] = useState(0)
  const [situation, setSituation] = useState('')
  const [targetTitles, setTargetTitles] = useState('')
  const [industry, setIndustry] = useState('')
  const [city, setCity] = useState('')
  const [hasReadBook, setHasReadBook] = useState('')
  const [saving, setSaving] = useState(false)

  async function finish() {
    setSaving(true)
    try {
      const { error } = await supabase.from('profiles').update({
        situation,
        target_titles: targetTitles,
        industry,
        city,
        has_read_book: hasReadBook === 'yes',
        onboarding_complete: true,
        day_started: new Date().toISOString(),
      }).eq('id', session.user.id)
      
      if (error) {
        console.error('Onboarding save error:', error)
        // Try upsert as fallback
        await supabase.from('profiles').upsert({
          id: session.user.id,
          situation,
          target_titles: targetTitles,
          industry,
          city,
          has_read_book: hasReadBook === 'yes',
          onboarding_complete: true,
          day_started: new Date().toISOString(),
        })
      }
    } catch (err) {
      console.error('Onboarding error:', err)
    }
    setSaving(false)
    onComplete()
  }

  const STEPS = [
    {
      question: 'Are you currently employed?',
      sub: 'This shapes the pace and approach of your plan.',
      options: [
        { value: 'unemployed', label: "No — I'm between jobs or was recently laid off", emoji: '🔍' },
        { value: 'employed', label: "Yes — I'm searching confidentially while working", emoji: '🤫' },
      ],
      value: situation, onChange: setSituation,
    },
    {
      question: 'What roles are you targeting?',
      sub: 'List your target job titles. You can update these anytime.',
      type: 'text', placeholder: 'e.g. Director of HR, VP of People, CHRO',
      value: targetTitles, onChange: setTargetTitles,
    },
    {
      question: "What industry and city are you searching in?",
      sub: "This fills in the blanks on every AI prompt in the app automatically, so you're not retyping it every day.",
      type: 'text2',
      fields: [
        { value: industry, onChange: setIndustry, placeholder: 'e.g. Healthcare, SaaS, Manufacturing' },
        { value: city, onChange: setCity, placeholder: 'e.g. Raleigh, NC' },
      ],
    },
    {
      question: 'Have you read The Application Multiplier?',
      sub: 'This helps us tailor your Day 1 experience.',
      options: [
        { value: 'yes', label: "Yes — I've read it and I'm ready to work the system", emoji: '📖' },
        { value: 'reading', label: "I'm reading it alongside the app", emoji: '📗' },
        { value: 'no', label: "Not yet — I found the app first", emoji: '✨' },
      ],
      value: hasReadBook, onChange: setHasReadBook,
    },
  ]

  const current = STEPS[step]
  const isLast = step === STEPS.length - 1
  const canContinue = [situation !== '', targetTitles.trim() !== '', true, hasReadBook !== ''][step]
  const progress = ((step + 1) / STEPS.length) * 100

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ width: '100%', maxWidth: '460px' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: 700, color: '#00712D' }}>The Application Multiplier</div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Let's get you set up</div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#aaa', marginBottom: '0.4rem' }}>
            <span>Question {step + 1} of {STEPS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div style={{ height: '4px', background: '#e8e8e8', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: '#00712D', transition: 'width 0.3s ease' }} />
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '2rem' }}>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.25rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.4rem', lineHeight: 1.3 }}>{current.question}</h2>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>{current.sub}</p>

          {current.options && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
              {current.options.map(opt => (
                <button key={opt.value} onClick={() => current.onChange(opt.value)} style={{ padding: '1rem', border: `2px solid ${current.value === opt.value ? '#00712D' : '#e8e8e8'}`, borderRadius: '8px', background: current.value === opt.value ? '#E8F2EC' : '#fff', textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{opt.emoji}</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: current.value === opt.value ? 600 : 400, color: current.value === opt.value ? '#00712D' : '#1A1A1A', lineHeight: 1.4 }}>{opt.label}</span>
                </button>
              ))}
            </div>
          )}

          {current.type === 'text' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <input className="input-field" placeholder={current.placeholder} value={current.value} onChange={e => current.onChange(e.target.value)} autoCorrect="off" style={{ fontSize: '0.95rem' }} onKeyDown={e => e.key === 'Enter' && canContinue && (isLast ? finish() : setStep(step + 1))} />
              <p style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.5rem' }}>Separate multiple titles with commas. You can edit this anytime.</p>
            </div>
          )}

          {current.type === 'text2' && (
            <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {current.fields.map((f, i) => (
                <input key={i} className="input-field" placeholder={f.placeholder} value={f.value} onChange={e => f.onChange(e.target.value)} autoCorrect="off" style={{ fontSize: '0.95rem' }} />
              ))}
              <p style={{ fontSize: '0.75rem', color: '#aaa' }}>Optional — you can skip this and fill it in later from your profile.</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {step > 0 && <button className="btn-ghost" onClick={() => setStep(step - 1)}>← Back</button>}
            <button className="btn-primary" style={{ flex: 1, padding: '0.85rem' }} onClick={() => isLast ? finish() : setStep(step + 1)} disabled={!canContinue || saving}>
              {saving ? 'Setting up your search...' : isLast ? "Let's go →" : 'Continue →'}
            </button>
          </div>
        </div>
        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#aaa', marginTop: '1rem' }}>4 quick questions. Everything is editable later.</p>
      </div>
    </div>
  )
}
